import { createRouter, createWebHistory } from "vue-router";
import { useStore } from "vuex";
import { routePermissionMap } from '@/utils/acl.js';
import authApi from '@/api/auth';
import userApi from '@/api/user';
import { useTitle } from '@/utils/useTitle.js';

// Lazy loading views
const Dashboard = () => import(/* webpackChunkName: "dashboard" */ "../views/common/Dashboard.vue");

const Profile = () => import(/* webpackChunkName: "profile" */ "../views/common/Profile.vue");
const Signin = () => import(/* webpackChunkName: "signin" */ "../views/common/Signin.vue");
const AccessDenied = () => import(/* webpackChunkName: "access-denied" */ "../views/common/AccessDenied.vue");
const FeedbackList = () => import(/* webpackChunkName: "feedbacks" */ "@/views/common/FeedbackList.vue");
const TicketList = () => import(/* webpackChunkName: "tickets" */ "@/views/common/TicketList.vue");
const IntegrationList = () => import(/* webpackChunkName: "integrations" */ "@/views/common/IntegrationList.vue");
const NotificationList = () => import(/* webpackChunkName: "notifications" */ "@/views/common/NotificationList.vue");
const PermissionList = () => import(/* webpackChunkName: "permissions" */ "@/views/common/PermissionList.vue");
const ModuleList = () => import(/* webpackChunkName: "modules" */ "@/views/common/ModuleList.vue");
const RoleList = () => import(/* webpackChunkName: "roles" */ "@/views/common/RoleList.vue");
const ServiceList = () => import(/* webpackChunkName: "services" */ "@/views/common/ServiceList.vue");
const ServiceFlowList = () => import(/* webpackChunkName: "service-flow-templates" */ "@/views/common/ServiceFlowList.vue");
const ServiceFlowInstanceList = () => import(/* webpackChunkName: "service-flows" */ "@/views/common/ServiceFlowInstanceList.vue");
const UserRoleList = () => import(/* webpackChunkName: "user-roles" */ "@/views/common/UserRoleList.vue");
const CustomerList = () => import(/* webpackChunkName: "customers" */ "@/views/common/CustomerList.vue");
const UserList = () => import(/* webpackChunkName: "users" */ "@/views/common/UserList.vue");
const AttendanceList = () => import(/* webpackChunkName: "attendance" */ "@/views/attendance/Index.vue");
const ServiceFlowStepList = () => import(/* webpackChunkName: "service-flow-steps" */ "@/views/common/ServiceFlowStepList.vue");
const ServiceFlowStepFieldList = () => import(/* webpackChunkName: "service-flow-step-fields" */ "@/views/common/ServiceFlowStepFieldList.vue");
const ServiceFlowStepFieldInstanceList = () => import(/* webpackChunkName: "service-flow-step-field-instances" */ "@/views/common/ServiceFlowStepFieldInstanceList.vue");
const ServiceFlowStepInstanceList = () => import(/* webpackChunkName: "service-flow-step-instances" */ "@/views/common/ServiceFlowStepInstanceList.vue");
const FileList = () => import(/* webpackChunkName: "documents" */ "../views/common/FileList.vue");
const ActivityLogList = () => import(/* webpackChunkName: "activity-logs" */ "@/views/common/ActivityLogList.vue");
const ModuleHelpList = () => import(/* webpackChunkName: "module-helps" */ "@/views/common/ModuleHelpList.vue");
const SystemSettings = () => import(/* webpackChunkName: "system-settings" */ "../views/admin/SystemSettings.vue");
const CompleteProfile = () => import(/* webpackChunkName: "complete-profile" */ "../views/common/CompleteProfile.vue");
const Approvals = () => import(/* webpackChunkName: "approvals" */ "@/views/common/Approvals.vue");
const FormHub = () => import(/* webpackChunkName: "form-hub" */ "@/views/common/FormHub.vue");
const NotFound = () => import(/* webpackChunkName: "not-found" */ "@/views/common/NotFound.vue");
const DemoReport = () => import(/* webpackChunkName: "demos-report" */ "@/views/common/DemoReport.vue");


const routes = [
  { path: "/", name: "/", redirect: "/dashboard" },
  { path: "/Demos/demos-report.html", component: DemoReport, name: "DemoReport" },
  { path: "/profile", component: Profile, name: "Profile" },

  { path: "/access-denied", component: AccessDenied, name: "AccessDenied" },
  { path: "/signin", component: Signin, name: "Signin" },
  { path: "/attendance", component: AttendanceList, name: "Attendance" },
  { path: "/system-settings", component: SystemSettings, name: "System Settings" },
  { path: "/dashboard", component: Dashboard, name: "Dashboard" },
  { path: "/service-flows", component: ServiceFlowInstanceList, name: "Service Flows" },
  { path: "/service-flows/:id", component: ServiceFlowInstanceList, name: "Service Flow Form", props: true },
  { path: "/service-flow-templates/:id", component: ServiceFlowList, name: "Service Flow Template Form", props: true },
  { path: "/form", component: FormHub, name: "Forms Hub" },
  { path: "/form/service-flows/:id", component: ServiceFlowInstanceList, name: "Form Service Flow Form", props: true },
  { path: "/form/service-flow-templates/:id", component: ServiceFlowList, name: "Form Service Flow Templates", props: true },
  { path: "/notifications", component: NotificationList, name: "Notifications" },
  { path: "/approvals", component: Approvals, name: "Approvals" },
  { path: "/customers", component: CustomerList, name: "Customers" },
  { path: "/feedbacks", component: FeedbackList, name: "Feedbacks" },
  { path: "/tickets", component: TicketList, name: "Tickets" },
  { path: "/services", component: ServiceList, name: "Services" },
  { path: "/service-flow-templates", component: ServiceFlowList, name: "Service Flow Templates" },
  { path: "/roles", component: RoleList, name: "Roles" },
  { path: "/users", component: UserList, name: "User List" },
  { path: "/permissions", component: PermissionList, name: "Permissions" },
  { path: "/modules", component: ModuleList, name: "Modules" },
  { path: "/module-helps", component: ModuleHelpList, name: "Help & FAQs" },
  { path: "/integrations", component: IntegrationList, name: "Integrations" },
  { path: "/service-flow-steps", component: ServiceFlowStepList, name: "Tab: Service Flow Steps" },
  { path: "/service-flow-step-fields", component: ServiceFlowStepFieldList, name: "Tab: Service Flow Step Fields" },
  { path: "/service-flow-step-field-instances", component: ServiceFlowStepFieldInstanceList, name: "Tab: Service Flow Step Field Instances" },
  { path: "/service-flow-step-instances", component: ServiceFlowStepInstanceList, name: "Tab: Service Flow Step Instances" },
  { path: "/activity-logs", component: ActivityLogList, name: "Activity Logs" },
  { path: "/documents", component: FileList, name: "Documents" },
  { path: "/complete-profile", component: CompleteProfile, name: "Complete Profile" },
  { path: "/reset-password", component: () => import("../views/common/CompleteProfile.vue"), name: "Reset Password" },
  { path: "/logout", component: Signin, name: "Logout" },
  { path: "/404", component: NotFound, name: "NotFound" },

  { path: "/:pathMatch(.*)*", redirect: "/404" }
];

let base = '/main/';
if (window.location.pathname.startsWith('/form/')) {
  base = '/form/';
} else if (window.location.pathname.startsWith('/guest/')) {
  base = '/guest/';
}

const router = createRouter({
  history: createWebHistory(base),
  routes,
  linkActiveClass: "active",
});

// async function SetPageTitle(store, name) {
//   await store.commit("setAppTitle", name);
// }

// Helper: Map route names/paths to required ACL permissions
const routeAclMap = routePermissionMap;
const { setTitle } = useTitle();

router.beforeEach(async (to, from, next) => {
  const store = useStore();
  // Allow sign in and error pages
  const guestRouteNames = ['Signin', 'AccessDenied', 'Complete Profile', 'Reset Password', 'NotFound', 'DemoReport', 'Guest Portal'];
  if (guestRouteNames.includes(to.name)) {
    setTitle(to.name);
    next();
    return;
  }
  // If not authenticated, try to restore state
  if (!store.state.isAuthenticated) {
    try {
      // Just call runAuth, it will use the cookie
      await authApi.runAuth(store, router, userApi);
      setTitle(to.name);
      next();
      return;
    } catch (e) {
      setTitle('Signin');
      window.location.href = `/signin?redirect=${encodeURIComponent(to.fullPath)}`;
      return;
    }
  } else {
    // ACL check
    const acl = store.state.acl;
    const roles = store.state.user?.roles || [];
    
    if (roles.length === 0 && to.name !== 'AccessDenied') {
      setTitle('Access Denied');
      next('/access-denied');
      return;
    }

    const requiredPermission = routeAclMap[to.path];
    if (requiredPermission && (!acl || !acl.includes(requiredPermission))) {
      setTitle('Access Denied');
      next('/access-denied');
      return;
    }
    setTitle(to.name);
    next();
  }
});

// Intercept ChunkLoadError or resource load failures due to a new build deployment
router.onError((error) => {
  const isChunkError = error.message && (
    error.message.includes('ChunkLoadError') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module')
  );
  if (isChunkError) {
    console.warn('Router dynamic chunk loading failure detected. Dispatched sw-force-update.');
    window.dispatchEvent(new CustomEvent('sw-force-update'));
  }
});

export default router;